import React, { useState, useMemo } from 'react';
import { Table, Button, Space, App, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined, SubnodeOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import BranchFormModal from './BranchFormModal';
import BranchLocationMapSettings from '../branch/BranchLocationMapSettings';
import { BranchDisplay } from '../common';
import type { ColumnsType } from 'antd/es/table';
import { withDevModalFilePath } from '../common';
import type { BranchHierarchy } from '../../types/company';

interface BranchManagementTabProps {
  companyId?: string;
  isManager: boolean;
  canAdd?: boolean;      // 追加権限（デフォルト: isManager）
  canEditMap?: boolean;  // マップ編集権限（デフォルト: isManager）
}

interface Branch {
  id: string;
  companyId: string;
  code: string;
  displayId?: string;
  name: string;
  nameKana?: string;
  type?: string;
  parentBranchId?: string | null;
  level: number;
  path?: string;
  sortOrder: number;
  prefecture?: string;
  city?: string;
  address?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  managerEmail?: string;
  employeeCount?: number;
  equipmentCount?: number;
  partCount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type BranchFormValues = Partial<Omit<Branch, 'id' | 'createdAt' | 'updatedAt'>> & {
  isActive?: boolean;
  displayId?: string;
  parentBranchId?: string | null;
};

const { Modal } = withDevModalFilePath('client/src/components/company/BranchManagementTab.tsx');

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    const messageValue = (error as { message?: unknown }).message;
    if (typeof messageValue === 'string') {
      return messageValue;
    }
  }
  return '予期しないエラーが発生しました';
};

const removeNullish = <T extends Record<string, unknown>>(obj: T) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      (acc as Record<string, unknown>)[key] = value;
    }
    return acc;
  }, {} as Partial<T>);
};

const prepareInitialValues = (branch: Branch): BranchFormValues => {
  const sanitized = removeNullish({
    ...branch,
    id: undefined,
    createdAt: undefined,
    updatedAt: undefined,
  }) as BranchFormValues;
  if (!sanitized.displayId && branch.displayId) {
    sanitized.displayId = branch.displayId;
  }
  if (!sanitized.displayId && branch.code) {
    sanitized.displayId = branch.code;
  }
  return sanitized;
};

// Generate tree line prefix for hierarchical display
const getTreePrefix = (level: number): React.ReactNode => {
  if (level === 0) return null;

  return (
    <span className="text-gray-400 mr-2" style={{ fontFamily: 'monospace' }}>
      {'│  '.repeat(level - 1)}├─{' '}
    </span>
  );
};

const BranchManagementTab: React.FC<BranchManagementTabProps> = ({
  companyId,
  isManager,
  canAdd = isManager,
  canEditMap = isManager,
}) => {
  const { message: messageApi } = App.useApp();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [defaultParentId, setDefaultParentId] = useState<string | null>(null);
  const [locationMapModalVisible, setLocationMapModalVisible] = useState(false);
  const [selectedBranchForMap, setSelectedBranchForMap] = useState<Branch | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const utils = trpc.useUtils();

  // 支店一覧の取得（階層構造）
  const { data: hierarchyData, isLoading } = trpc.company.branches.listHierarchy.useQuery(
    { companyId: companyId || '', isActive: undefined },
    { enabled: !!companyId }
  );

  // フラットな支店リスト（親支店選択用）
  const { data: flatBranches } = trpc.company.branches.list.useQuery(
    { companyId: companyId || '', isActive: undefined },
    { enabled: !!companyId }
  );

  // Convert hierarchy to flat list with children for Table
  const tableData = useMemo(() => {
    if (!hierarchyData) return [];

    const flattenHierarchy = (items: BranchHierarchy[]): Branch[] => {
      const result: Branch[] = [];
      for (const item of items) {
        const { children, ...branchData } = item;
        result.push({
          ...branchData,
          code: branchData.displayId || '',
        } as Branch);
        if (children && children.length > 0) {
          result.push(...flattenHierarchy(children));
        }
      }
      return result;
    };

    return flattenHierarchy(hierarchyData as BranchHierarchy[]);
  }, [hierarchyData]);

  // Auto-expand all rows by default
  useMemo(() => {
    if (tableData.length > 0 && expandedRowKeys.length === 0) {
      const parentIds = tableData
        .filter(b => tableData.some(child => child.parentBranchId === b.id))
        .map(b => b.id);
      setExpandedRowKeys(parentIds);
    }
  }, [tableData, expandedRowKeys.length]);

  // Build tree structure for Table expandable
  const treeData = useMemo(() => {
    if (!hierarchyData) return [];

    const buildTreeData = (items: BranchHierarchy[]): (Branch & { children?: Branch[] })[] => {
      return items.map(item => {
        const { children, ...branchData } = item;
        const branch: Branch & { children?: Branch[] } = {
          ...branchData,
          code: branchData.displayId || '',
        } as Branch;

        if (children && children.length > 0) {
          branch.children = buildTreeData(children);
        }

        return branch;
      });
    };

    return buildTreeData(hierarchyData as BranchHierarchy[]);
  }, [hierarchyData]);

  // 支店作成
  const createMutation = trpc.company.branches.create.useMutation({
    onSuccess: () => {
      messageApi.success('支店を登録しました');
      setModalVisible(false);
      setEditingBranch(null);
      setDefaultParentId(null);
      utils.company.branches.list.invalidate();
      utils.company.branches.listHierarchy.invalidate();
    },
    onError: (error: unknown) => {
      messageApi.error(`登録に失敗しました: ${getErrorMessage(error)}`);
    },
  });

  // 支店更新
  const updateMutation = trpc.company.branches.update.useMutation({
    onSuccess: () => {
      messageApi.success('支店情報を更新しました');
      setModalVisible(false);
      setEditingBranch(null);
      setDefaultParentId(null);
      utils.company.branches.list.invalidate();
      utils.company.branches.listHierarchy.invalidate();
    },
    onError: (error: unknown) => {
      messageApi.error(`更新に失敗しました: ${getErrorMessage(error)}`);
    },
  });

  // 支店削除
  const deleteMutation = trpc.company.branches.delete.useMutation({
    onSuccess: () => {
      messageApi.success('支店を削除しました');
      utils.company.branches.list.invalidate();
      utils.company.branches.listHierarchy.invalidate();
    },
    onError: (error: unknown) => {
      messageApi.error(`削除に失敗しました: ${getErrorMessage(error)}`);
    },
  });

  const handleAdd = () => {
    setEditingBranch(null);
    setDefaultParentId(null);
    setModalVisible(true);
  };

  const handleAddChild = (parentBranch: Branch) => {
    setEditingBranch(null);
    setDefaultParentId(parentBranch.id);
    setModalVisible(true);
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setDefaultParentId(null);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  // メインブランチかどうかを判定
  const isMainBranch = (branch: Branch): boolean => {
    // displayIdがCB-1/CB-00001、またはtypeが本社の場合はメインブランチ
    const displayIdToCheck = branch.displayId || branch.code;
    const isMainDisplayId =
      displayIdToCheck === 'CB-1' || displayIdToCheck === 'CB-00001';
    return isMainDisplayId || branch.type === '本社';
  };

  // Can add child (max 3 levels: 0, 1, 2)
  const canAddChild = (branch: Branch): boolean => {
    return branch.level < 2;
  };

  // Has children
  const hasChildren = (branch: Branch): boolean => {
    return tableData.some(b => b.parentBranchId === branch.id);
  };

  const handleLocationMapSettings = (branch: Branch) => {
    setSelectedBranchForMap(branch);
    setLocationMapModalVisible(true);
  };

  const handleModalSubmit = async (values: BranchFormValues) => {
    const sanitizedValues = removeNullish(values) as BranchFormValues;

    if (editingBranch) {
      await updateMutation.mutateAsync({
        id: editingBranch.id,
        data: sanitizedValues,
      });
    } else if (companyId) {
      await createMutation.mutateAsync({
        ...sanitizedValues,
        companyId,
      });
    } else {
      messageApi.error('会社情報が見つかりません');
    }
  };

  const initialFormValues = editingBranch ? prepareInitialValues(editingBranch) : undefined;

  const columns: ColumnsType<Branch> = [
    {
      title: '拠点情報',
      key: 'branch',
      render: (_, record) => (
        <div className="flex items-center">
          {getTreePrefix(record.level)}
          <BranchDisplay
            branch={{
              id: record.id,
              displayId: record.displayId ?? record.code,
              name: record.name,
              type: record.type,
              isActive: record.isActive,
            }}
            showDisplayId={false}
            showType
          />
        </div>
      ),
    },
    {
      title: '所在地',
      key: 'location',
      render: (_, record) => (
        <div>
          {record.prefecture && record.city && (
            <div>{record.prefecture} {record.city}</div>
          )}
          {record.address && (
            <div className="text-xs text-gray-500">{record.address}</div>
          )}
        </div>
      ),
    },
    {
      title: '管理者',
      dataIndex: 'managerName',
      key: 'managerName',
      width: 150,
      render: (name, record) => (
        <div>
          {name && <div>{name}</div>}
          {record.managerEmail && (
            <div className="text-xs text-gray-500">{record.managerEmail}</div>
          )}
        </div>
      ),
    },
    {
      title: '従業員数',
      dataIndex: 'employeeCount',
      key: 'employeeCount',
      width: 80,
      align: 'right',
      render: (count) => count ? `${count}名` : '-',
    },
    {
      title: '設備数',
      dataIndex: 'equipmentCount',
      key: 'equipmentCount',
      width: 80,
      align: 'right',
      render: (count) => count ?? 0,
    },
    {
      title: '部品・消耗品',
      dataIndex: 'partCount',
      key: 'partCount',
      width: 100,
      align: 'right',
      render: (count) => count ?? 0,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          {canAdd && canAddChild(record) && (
            <Tooltip title="子拠点を追加">
              <Button
                type="text"
                icon={<SubnodeOutlined />}
                onClick={() => handleAddChild(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="エリアマップ設定">
            <Button
              type="text"
              icon={<PictureOutlined />}
              onClick={() => handleLocationMapSettings(record)}
            />
          </Tooltip>
          <Tooltip title="編集">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!isManager}
            />
          </Tooltip>
          <Popconfirm
            title="削除確認"
            description={
              isMainBranch(record)
                ? "メインブランチ（本社）は削除できません"
                : hasChildren(record)
                  ? "子拠点がある場合は先に削除してください"
                  : "この支店を削除してもよろしいですか？"
            }
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
            okButtonProps={{ danger: true }}
            disabled={isMainBranch(record) || hasChildren(record)}
          >
            <Tooltip title={
              isMainBranch(record)
                ? "メインブランチ（本社）は削除できません"
                : hasChildren(record)
                  ? "子拠点を先に削除してください"
                  : "削除"
            }>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={isMainBranch(record) || hasChildren(record)}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!companyId) {
    return <div>会社情報を読み込み中...</div>;
  }

  return (
    <div>
      {canAdd && (
        <div className="mb-4">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            ルート拠点を追加
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={treeData}
        rowKey="id"
        loading={isLoading}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          indentSize: 0, // We handle indentation manually with tree prefix
        }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `全 ${total} 件`,
        }}
        locale={{
          emptyText: 'マップが登録されていません',
        }}
      />

      <BranchFormModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingBranch(null);
          setDefaultParentId(null);
        }}
        onSubmit={handleModalSubmit}
        initialValues={initialFormValues}
        loading={createMutation.isPending || updateMutation.isPending}
        branches={flatBranches as Branch[] || []}
        defaultParentId={defaultParentId}
      />

      <Modal
        title={`エリアマップ設定 - ${selectedBranchForMap?.name || ''}`}
        open={locationMapModalVisible}
        onCancel={() => {
          setLocationMapModalVisible(false);
          setSelectedBranchForMap(null);
        }}
        footer={null}
        width={800}
      >
        {selectedBranchForMap && (
          <BranchLocationMapSettings
            branchId={selectedBranchForMap.id}
            canEdit={canEditMap}
          />
        )}
      </Modal>
    </div>
  );
};

export default BranchManagementTab;
